import LASInterface from '@/appLogic/workbench/LAS/LASInterface'
import XandrInterface from '@/appLogic/workbench/Xandr/XandrInterface'
import transformAttributes from '@/components/common/attributes'
import {
  SET_ATTRIBUTES,
  SET_FAMILY_ATTRIBUTES,
  SET_INTERFACE,
  SET_SLOTTED_VALUE,
  SET_TARGET,
  SET_ENABLE_DOWN_STREAM,
  SET_ENABLE_SAVE_BUTTON,
  SET_LOADING_WORKBENCH,
  SET_TARGET_DEFINITION_DIRTY,
  RESET_STORE,
  SET_ENABLE_DEFINITION_ACTIONS,
  CLEAR_ATTRIBUTES,
  SHOW_AUP_MODAL,
  SET_ADVERTISER,
  SET_DATASET_FAMILY,
  STORE_NAME,
  UPDATE_TARGET_NAME,
  SET_AVAILABLE_DESTINATIONS,
  SET_TARGET_CREATE_ACTION,
} from './actions'
import { WORKBENCH_DEFAULT_STATE } from './index'
import { registerIfNotExist, unregisterIfExists } from '@/store'
import {
  LAS_DESTINATIONS,
  ADDRESSABLE_DESTINATIONS,
  DIGITAL_DESTINATIONS,
  DESTINATIONS,
  ACTIVATION_STATUS,
  TARGET_TYPE,
} from '@/appLogic/targets/Constants'

const intersectingDatasets = function (datasetGroups) {
  let intersection = [...datasetGroups[0]]
  for (let i = 1; i < datasetGroups.length; i++) {
    intersection = intersection.filter(d => datasetGroups[i].includes(d))
  }
  return intersection.sort((a, b) => {
    if (a > b) return 1
    else return -1
  })
}

const getFamilyDatasetsFromDataset = function (datasets, datasetFamilies) {
  let datasetGroups = []
  datasets.forEach(dataset => {
    const relatedDatasets = datasetFamilies.datasetRelationships[dataset]
    datasetGroups.push([dataset, ...relatedDatasets])
  })

  let result = intersectingDatasets(datasetGroups)
  return result
}

const filterBYODDatasetByFamily = function (byodDataset, datasetFamilies) {
  // Create a clone of the dataset but initially without children
  const filteredBYODDataset = { ...byodDataset }
  delete filteredBYODDataset.children

  // Process children if they exist
  if (byodDataset.children && byodDataset.children.length > 0) {
    const filteredChildren = []

    for (const child of byodDataset.children) {
      const filteredChild = filterBYODDatasetByFamily(child, datasetFamilies)
      if (filteredChild) {
        filteredChildren.push(filteredChild)
      }
    }

    if (filteredChildren.length > 0) {
      filteredBYODDataset.children = filteredChildren
    }
  }

  // Include this dataset if it has children or fits the family criteria
  if (filteredBYODDataset.children || datasetFamilies.includes(byodDataset.rootFamily)) {
    return filteredBYODDataset
  }

  return null
}

const mutations = {
  [SET_TARGET]: (state, target) => {
    state.workbenchTarget = target
  },
  [SET_TARGET_CREATE_ACTION]: (state, action) => {
    state.targetCreateAction = action
  },
  [SET_ATTRIBUTES]: (state, attributes) => {
    state.attributes = attributes
  },
  [SET_DATASET_FAMILY]: (state, families) => {
    state.datasetFamilies = families
  },
  [SET_AVAILABLE_DESTINATIONS]: (state, destinations) => {
    state.availableDestinations = destinations
  },
  /**
   * this will set family attributes and other things like the interface
   * @param {*} state
   * @param {object} payload
   * @param {object} payload.context
   * @param {string} payload.family - the family comes from the file downloaded from S3
   */
  [SET_FAMILY_ATTRIBUTES]: (state, payload) => {
    if (state.interface) {
      let modules = state.interface.getStoreModules()
      for (let mod of modules) {
        unregisterIfExists([STORE_NAME, mod.name])
      }
    }
    state.selectedFamilyDataset = payload.family ? payload.family[0] : payload.family
    const context = payload.context
    const byodDataset = context.rootGetters.getBYODDataset
    if (!state.availableAttributes) {
      state.availableAttributes = state.attributes
    }
    if (payload.family) {
      const selectedDatasets = getFamilyDatasetsFromDataset(payload.family, state.datasetFamilies)
      const newAttributes = []
      for (let attr of state.availableAttributes) {
        // attr.data.id contains the id (dataset name)
        if (attr.data.name.toLowerCase() === TARGET_TYPE.BYOD.toLowerCase()) {
          let filteredBYODDataset = filterBYODDatasetByFamily(byodDataset, selectedDatasets)
          if (filteredBYODDataset && filteredBYODDataset.children && filteredBYODDataset.children.length > 0) {
            newAttributes.push(transformAttributes(filteredBYODDataset, filteredBYODDataset.id, false))
          }
        } else if (selectedDatasets.includes(attr.data.id)) {
          newAttributes.push(attr)
        }
      }
      let dataSetFamilyChanged = !(
        state.selectedDatasets &&
        state.selectedDatasets.length === selectedDatasets.length &&
        state.selectedDatasets.every(function (d) {
          return selectedDatasets.indexOf(d) !== -1
        })
      )
      let isCDPDatasource = [
        'xandr',
        TARGET_TYPE.ADSTRA,
        TARGET_TYPE.PRISM,
        TARGET_TYPE.DNB,
        TARGET_TYPE.MAX,
        TARGET_TYPE.TVE,
        TARGET_TYPE.BYOD,
        TARGET_TYPE.EPSILON,
        TARGET_TYPE.DISCOVERY_PLUS,
        TARGET_TYPE.DCR,
        TARGET_TYPE.VIDEOAMP,
        TARGET_TYPE.LIVERAMP,
        TARGET_TYPE.WBGAMES,
        TARGET_TYPE.RETARGETING,
        TARGET_TYPE.FREEWHEEL,
        TARGET_TYPE.GOOGLE_AD_MANAGER,
        TARGET_TYPE.LAL,
        TARGET_TYPE.DC_COMICS,
        TARGET_TYPE.CNN,
      ].includes(payload.family[0])
      if (dataSetFamilyChanged) {
        state.selectedDatasets = selectedDatasets
        context.commit(SET_ATTRIBUTES, newAttributes)
        // set the interface here
        if (isCDPDatasource) {
          state.interface = new XandrInterface()
          if (!state.workbenchTarget.xandr_data || state.workbenchTarget.xandr_data === undefined) {
            context.dispatch(SET_TARGET, { ...state.workbenchTarget, xandr_data: { frequency: null } })
          } else if (state.workbenchTarget.activation_status !== ACTIVATION_STATUS.DRAFT) {
            context.dispatch(SET_TARGET, state.workbenchTarget)
          }
        } else {
          state.interface = new LASInterface()
          state.availableDestinations = [...LAS_DESTINATIONS]
        }
      }

      if (
        payload.family.every(f => f === TARGET_TYPE.VIDEOAMP) ||
        payload.family.some(f => f === TARGET_TYPE.RETARGETING)
      ) {
        state.availableDestinations = [DESTINATIONS.FREEWHEEL]
      } else if (
        [TARGET_TYPE.LIVERAMP, TARGET_TYPE.VIDEOAMP, TARGET_TYPE.EPSILON, TARGET_TYPE.DCR].some(tt =>
          payload.family.includes(tt),
        )
      ) {
        state.availableDestinations = [DESTINATIONS.FREEWHEEL, DESTINATIONS.VIDEOAMP]
      } else if (
        [
          TARGET_TYPE.DNB,
          TARGET_TYPE.MAX,
          TARGET_TYPE.TVE,
          TARGET_TYPE.DISCOVERY_PLUS,
          TARGET_TYPE.WBGAMES,
          TARGET_TYPE.DC_COMICS,
          TARGET_TYPE.FREEWHEEL,
          TARGET_TYPE.GOOGLE_AD_MANAGER,
          TARGET_TYPE.LAL,
          TARGET_TYPE.CNN,
        ].some(tt => payload.family.includes(tt))
      ) {
        // set available destinations to FreeWheel and Google Ad Manager Destinations if DNB, Max, TVE or D+
        state.availableDestinations = [DESTINATIONS.FREEWHEEL, DESTINATIONS.GOOGLE_AD_MANAGER, DESTINATIONS.VIDEOAMP]
      } else if (isCDPDatasource) {
        // set Addressable and Digital Destinations
        state.availableDestinations = [...ADDRESSABLE_DESTINATIONS, ...DIGITAL_DESTINATIONS, DESTINATIONS.VIDEOAMP]
      }

      // TODO have to destroy any hanging store modules
      let modules = state.interface.getStoreModules()
      for (let mod of modules) {
        registerIfNotExist([STORE_NAME, mod.name], mod.module)
      }
      state.interface.setInterface(context)
    } else {
      state.selectedDatasets = []
      context.commit(SET_ATTRIBUTES, state.availableAttributes)
    }
  },
  [SET_INTERFACE]: (state, inter) => {
    state.interface = inter
  },
  [SET_SLOTTED_VALUE]: (state, slottedValue) => {
    state.slottedValue = slottedValue
  },
  [SET_ENABLE_DOWN_STREAM]: (state, setValue) => {
    state.enableDownstream = setValue
  },
  [SET_ENABLE_SAVE_BUTTON]: (state, value) => {
    state.enableSaving = value
  },
  [SET_LOADING_WORKBENCH]: (state, value) => {
    state.loadingWorkbench = value
  },
  [SET_TARGET_DEFINITION_DIRTY]: (state, value) => {
    state.dirty = value
  },
  [RESET_STORE]: state => {
    for (let key of Object.keys(WORKBENCH_DEFAULT_STATE)) {
      state[key] = WORKBENCH_DEFAULT_STATE[key]
    }
  },
  [CLEAR_ATTRIBUTES]: state => {
    state.attributes = null
  },
  [SET_ENABLE_DEFINITION_ACTIONS]: (state, value) => {
    state.enableDefinitionActions = value
  },
  [SHOW_AUP_MODAL]: (state, value) => {
    state.showAUPModal = value
  },
  [SET_ADVERTISER]: (state, advertiser) => {
    state.workbenchTarget.advertiser = advertiser
  },
  [UPDATE_TARGET_NAME]: (state, targetName) => {
    state.workbenchTarget.display_name = targetName
  },
}

export default mutations



// WEBPACK FOOTER //
// ./src/app/appLogic/workbench/common/store/mutations.js